import {
  CommonResultId,
  VolleyballEventStatusId,
  VolleyballIncidentId,
} from '@collector/sportsapi-client-legacy'

import {
  BlockingStrategy,
  ConfirmingStrategy,
  EventRelatedStatusConfig,
  EventRelatedStatusValue,
  FINISHED_DESCRIPTION,
} from '../../configuration'

const GOLDEN_SET_DESCRIPTION = 'Click if there will be a golden set'

const otherStatusIncidentsWithGoldenSet:
  VolleyballIncidentId | EventRelatedStatusValue<VolleyballIncidentId>[] = [
  VolleyballIncidentId.NotStarted,
  {
    incidentId: VolleyballIncidentId.GoldenSetStart,
    description: GOLDEN_SET_DESCRIPTION,
  },
  VolleyballIncidentId.FinishedAwardedWin,
  VolleyballIncidentId.Interrupted,
  VolleyballIncidentId.Postponed,
  VolleyballIncidentId.Abandoned,
  VolleyballIncidentId.Cancelled,
  VolleyballIncidentId.ToFinish,
]

const otherStatusIncidents: VolleyballIncidentId[] = [
  VolleyballIncidentId.NotStarted,
  VolleyballIncidentId.FinishedAwardedWin,
  VolleyballIncidentId.Interrupted,
  VolleyballIncidentId.Postponed,
  VolleyballIncidentId.Abandoned,
  VolleyballIncidentId.Cancelled,
  VolleyballIncidentId.ToFinish,
]

const confirmingStrategy: ConfirmingStrategy = {
  resultId: CommonResultId.Result,
  getConfirmationDescription(currentResult) {
    return `Do you want to finish the game with the current result ${currentResult?.home}:${currentResult?.away}?`
  },
}

const getBlockingStrategy: (setsPlayed: number) => BlockingStrategy = (
  setsPlayed: number,
) => ({
  resultId: CommonResultId.Result,
  unblockInstructions: 'Main result is incorrect - check it',
  isBlocked(currentResult) {
    if (
      currentResult.homeResult === undefined ||
      currentResult.awayResult === undefined
    ) {
      throw new Error('result is not defined')
    }

    return currentResult.homeResult + currentResult.awayResult !== setsPlayed
  },
})

export const eventRelatedStatusConfig: EventRelatedStatusConfig<
  VolleyballEventStatusId,
  VolleyballIncidentId
> = {
  [VolleyballEventStatusId.NotStarted]: {
    possibleNextStatusIncidents: [
      VolleyballIncidentId.FirstSetStarted,
      VolleyballIncidentId.StartDelayed,
    ],
    otherStatusIncidents: otherStatusIncidents.filter(
      (incidentId) => incidentId !== VolleyballIncidentId.NotStarted,
    ),
  },
  [VolleyballEventStatusId.StartDelayed]: {
    possibleNextStatusIncidents: [VolleyballIncidentId.FirstSetStarted],
    otherStatusIncidents,
  },
  [VolleyballEventStatusId.FirstSet]: {
    possibleNextStatusIncidents: [
      {
        incidentId: VolleyballIncidentId.BreakAfterFirstSet,
        blockingStrategy: getBlockingStrategy(1),
      },
    ],
    otherStatusIncidents,
  },
  [VolleyballEventStatusId.BreakAfterFirstSet]: {
    possibleNextStatusIncidents: [VolleyballIncidentId.SecondSetStarted],
    otherStatusIncidents,
  },
  [VolleyballEventStatusId.SecondSet]: {
    possibleNextStatusIncidents: [
      {
        incidentId: VolleyballIncidentId.BreakAfterSecondSet,
        blockingStrategy: getBlockingStrategy(2),
      },
    ],
    otherStatusIncidents,
  },
  [VolleyballEventStatusId.BreakAfterSecondSet]: {
    possibleNextStatusIncidents: [VolleyballIncidentId.ThirdSetStarted],
    otherStatusIncidents,
  },
  [VolleyballEventStatusId.ThirdSet]: {
    possibleNextStatusIncidents: [
      {
        incidentId: VolleyballIncidentId.BreakAfterThirdSet,
        blockingStrategy: getBlockingStrategy(3),
      },
      {
        incidentId: VolleyballIncidentId.Finished,
        description: FINISHED_DESCRIPTION,
        confirmingStrategy,
      },
    ],
    otherStatusIncidents,
  },
  [VolleyballEventStatusId.BreakAfterThirdSet]: {
    possibleNextStatusIncidents: [VolleyballIncidentId.FourthSetStarted],
    otherStatusIncidents: otherStatusIncidentsWithGoldenSet,
  },
  [VolleyballEventStatusId.FourthSet]: {
    possibleNextStatusIncidents: [
      {
        incidentId: VolleyballIncidentId.BreakAfterFourthSet,
        blockingStrategy: getBlockingStrategy(4),
      },
      {
        incidentId: VolleyballIncidentId.Finished,
        description: FINISHED_DESCRIPTION,
        confirmingStrategy,
      },
    ],
    otherStatusIncidents,
  },
  [VolleyballEventStatusId.BreakAfterFourthSet]: {
    possibleNextStatusIncidents: [VolleyballIncidentId.FifthSetStarted],
    otherStatusIncidents: otherStatusIncidentsWithGoldenSet,
  },
  [VolleyballEventStatusId.FifthSet]: {
    possibleNextStatusIncidents: [
      {
        incidentId: VolleyballIncidentId.Finished,
        description: FINISHED_DESCRIPTION,
        confirmingStrategy,
      },
      {
        incidentId: VolleyballIncidentId.BreakAfterFifthSet,
        blockingStrategy: getBlockingStrategy(5),
      },
    ],
    otherStatusIncidents,
  },
  [VolleyballEventStatusId.BreakAfterFifthSet]: {
    possibleNextStatusIncidents: [
      {
        incidentId: VolleyballIncidentId.GoldenSetStart,
        description: GOLDEN_SET_DESCRIPTION,
      },
    ],
    otherStatusIncidents,
  },
  [VolleyballEventStatusId.Finished]: {
    possibleNextStatusIncidents: [],
    otherStatusIncidents,
  },
  [VolleyballEventStatusId.Interrupted]: {
    possibleNextStatusIncidents: [],
    otherStatusIncidents,
  },
  [VolleyballEventStatusId.Postponed]: {
    possibleNextStatusIncidents: [],
    otherStatusIncidents,
  },
  [VolleyballEventStatusId.Abandoned]: {
    possibleNextStatusIncidents: [],
    otherStatusIncidents,
  },
  [VolleyballEventStatusId.Cancelled]: {
    possibleNextStatusIncidents: [],
    otherStatusIncidents,
  },
  [VolleyballEventStatusId.ToFinish]: {
    possibleNextStatusIncidents: [],
    otherStatusIncidents,
  },
  // TODO: There will be another task for GoldenSet support
  [VolleyballEventStatusId.GoldenSet]: {
    possibleNextStatusIncidents: [
      {
        incidentId: VolleyballIncidentId.Finished,
        description: FINISHED_DESCRIPTION,
        confirmingStrategy,
      },
    ],
    otherStatusIncidents,
  },
}
